// In-app fallback for "Stop receiving this task by email" links.
// New emails use a signed backend one-click URL; this page covers older
// emails that pointed at `/tasks/:taskId/email-settings` and logged-in users
// who land here from the app.
import React, { useCallback, useEffect, useState } from 'react';
import { View, StyleSheet, ActivityIndicator, Platform } from 'react-native';
import { Redirect, useLocalSearchParams, useRouter } from 'expo-router';
import { useTheme } from '@/context/ThemeContext';
import { useTranslation } from '@/hooks/useTranslation';
import Text from '@/components/Typography/Text';
import Button from '@/components/Buttons/Button';
import MetaHead from '@/components/MetaHead';
import { useAuthStore, useInferenceTasksStore, useToastStore } from '@/store';
import { inferenceTasksApi } from '@/lib/api';

export default function TaskEmailSettingsRoute() {
  const { taskId: taskIdParam } = useLocalSearchParams();
  const taskId = Array.isArray(taskIdParam) ? taskIdParam[0] : taskIdParam;
  const router = useRouter();
  const { colors } = useTheme();
  const { t } = useTranslation();
  const hasUser = useAuthStore((s) => !!s.user);
  const getTaskById = useInferenceTasksStore((s) => s.getTaskById);
  const updateTask = useInferenceTasksStore((s) => s.updateTask);
  const addTasks = useInferenceTasksStore((s) => s.addTasks);
  const { addToast } = useToastStore((s) => s.actions);

  const [task, setTask] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      if (!taskId || !hasUser) {
        if (!cancelled) {
          setIsLoading(false);
        }
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const stored = getTaskById(taskId);
        if (stored) {
          if (!cancelled) setTask(stored);
          return;
        }

        const fetched = await inferenceTasksApi.fetchTask(taskId);
        if (cancelled) return;
        addTasks(fetched);
        setTask(fetched);
      } catch (err) {
        if (!cancelled) {
          setError(err?.message || t('tasks.failedToLoadTask'));
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [taskId, hasUser, getTaskById, addTasks, t]);

  const handleDisableEmail = useCallback(async () => {
    if (!taskId || isSaving) return;
    setIsSaving(true);
    try {
      const updated = await updateTask(taskId, {
        emailDelivery: { isEnabled: false },
      });
      setTask(updated);
      setDone(true);
      addToast(t('pages.tasks.emailDisabledSuccess'), 'success');
    } catch (err) {
      addToast(err?.message || t('pages.tasks.emailDisabledFailed'), 'error');
    } finally {
      setIsSaving(false);
    }
  }, [taskId, isSaving, updateTask, addToast, t]);

  if (!taskId) {
    return <Redirect href="/tasks" />;
  }

  if (!hasUser) {
    return <Redirect href="/login" />;
  }

  const emailAlreadyOff = task?.emailDelivery?.isEnabled === false;
  const styles = createStyles(colors);

  return (
    <View style={styles.root}>
      <MetaHead
        title={t('pages.tasks.emailSettingsTitle')}
        description={t('pages.tasks.emailSettingsDescription')}
      />
      <View style={styles.card}>
        {isLoading ? (
          <ActivityIndicator color={colors.accent_color_primary} />
        ) : error ? (
          <>
            <Text style={styles.title}>{t('tasks.taskNotFound')}</Text>
            <Text style={styles.body}>{error}</Text>
            <Button
              text={t('pages.tasks.backToTasks')}
              onPress={() => router.replace('/tasks')}
              style={styles.button}
            />
          </>
        ) : done || emailAlreadyOff ? (
          <>
            <Text style={styles.title}>{t('pages.tasks.emailDisabledTitle')}</Text>
            <Text style={styles.body}>
              {emailAlreadyOff && !done
                ? t('pages.tasks.emailAlreadyDisabled')
                : t('pages.tasks.emailDisabledBody')}
            </Text>
            {!!task?.name && <Text style={styles.taskName}>{task.name}</Text>}
            <Button
              text={t('pages.tasks.manageThisTask')}
              onPress={() => router.replace(`/tasks?id=${encodeURIComponent(taskId)}`)}
              style={styles.button}
            />
            <Button
              text={t('pages.tasks.backToTasks')}
              variant="secondary"
              onPress={() => router.replace('/tasks')}
              style={styles.button}
            />
          </>
        ) : (
          <>
            <Text style={styles.title}>{t('pages.tasks.emailSettingsTitle')}</Text>
            <Text style={styles.body}>{t('pages.tasks.emailSettingsBody')}</Text>
            {!!task?.name && <Text style={styles.taskName}>{task.name}</Text>}
            <Button
              text={t('pages.tasks.stopTaskEmails')}
              onPress={handleDisableEmail}
              isLoading={isSaving}
              style={styles.button}
            />
            <Button
              text={t('pages.tasks.manageThisTask')}
              variant="secondary"
              onPress={() => router.replace(`/tasks?id=${encodeURIComponent(taskId)}`)}
              style={styles.button}
            />
          </>
        )}
      </View>
    </View>
  );
}

function createStyles(colors) {
  return StyleSheet.create({
    root: {
      flex: 1,
      alignItems: 'center',
      justifyContent: 'center',
      padding: 24,
      backgroundColor: colors.solid_color_primary,
    },
    card: {
      width: '100%',
      maxWidth: 440,
      alignItems: 'center',
      gap: 12,
      ...(Platform.OS === 'web' ? { textAlign: 'center' } : null),
    },
    title: {
      fontSize: 20,
      fontWeight: '600',
      color: colors.text_color_primary,
      textAlign: 'center',
    },
    body: {
      fontSize: 15,
      lineHeight: 22,
      color: colors.text_color_secondary,
      textAlign: 'center',
    },
    taskName: {
      fontSize: 14,
      color: colors.text_color_primary,
      textAlign: 'center',
      marginBottom: 4,
    },
    button: {
      alignSelf: 'stretch',
      marginTop: 4,
    },
  });
}
